import styled from '@emotion/styled'
import { Rumble } from 'app/components/Icons'
import { HeaderContext } from 'app/containers/Header'
import useMediaQuery from 'app/utils/useMediaQuery'
import { useVocabularyData } from 'app/utils/vocabulary'
import { circOut, motion } from 'framer-motion'
import React, { memo, useContext } from 'react'

import {
  Props as SecondaryMenuItemProps,
  SecondaryMenuItem,
} from './SecondaryMenuItem'

export interface Props {
  menuItems: SecondaryMenuItemProps[]
  modalStatus?: boolean
  startAnimation?: boolean
}

export const SecondaryMenu = memo(function SecondaryMenu({
  menuItems,
  modalStatus,
  startAnimation,
}: Props) {
  if (!menuItems) {
    return null
  }

  const { languageCode, languagePrefix } = useContext(HeaderContext) as any

  const isMobile = useMediaQuery('(max-width: 1199px)')

  const variants = {
    visible: (i: any) => ({
      opacity: 1,
      y: 0,
      transition: {
        bounce: 0,
        delay: i * 0.07 + (isMobile ? 0.45 : 0),
        duration: 0.7,
        ease: circOut,
        type: 'spring',
      },
    }),
    hidden: {
      opacity: 0,
      y: 30,
      transition: {
        delay: 0,
        duration: 0,
      },
    },
  }

  return (
    <Container>
      <List>
        {menuItems.map((item, index) => (
          <Li
            key={index}
            custom={index}
            animate={modalStatus && startAnimation ? 'visible' : 'hidden'}
            variants={variants}
          >
            <SecondaryMenuItem {...item} />
          </Li>
        ))}

        <Li
          custom={menuItems.length + 1}
          animate={modalStatus && startAnimation ? 'visible' : 'hidden'}
          variants={variants}
        >
          <BookingCTA
            rel="noopener"
            target="_blank"
            href={`https://www.simplebooking.it/ibe/hotelbooking/search?hid=2778&lang=${
              languagePrefix ? languagePrefix.toLocaleUpperCase() : 'EN'
            }`}
          >
            <Rumble />
            {useVocabularyData('book', languageCode)}
          </BookingCTA>
        </Li>
      </List>
    </Container>
  )
})

const Container = styled.nav``

const List = styled.ul``

const Li = styled(motion.li)`
  margin-top: 1.0625rem;
  &:first-of-type {
    margin-top: 0;
  }

  @media (max-width: 1199px) {
    margin-top: 1.875rem;
    &:first-of-type {
      margin-top: 1.875rem;
    }
  }
`

const BookingCTA = styled.a`
  display: inline-flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.variants.neutralLight5};
  font-family: ${({ theme }) => theme.fontFamily.heading};
  font-size: 1rem;
  font-weight: 500;
  line-height: 1.5rem;
  position: relative;
  transition: 0.4s ease-in-out;
  z-index: 2;
  &:hover {
    opacity: 0.4;
  }

  svg {
    width: 0.25rem;
    height: 0.25rem;
    fill: ${({ theme }) => theme.colors.variants.neutralLight5};
    margin-right: 0.625rem;
    transform: translateY(0.1875rem);
  }

  @media (max-width: 1199px) {
    font-size: 2.25rem;
    font-weight: 300;
    line-height: 2.625rem;

    svg {
      display: none;
    }
  }
`
